import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import LeadFormButton from '../../../components/LeadFormButton';
import { useLeadForm } from '../../../contexts/LeadFormContext';

const VirtualShowroomSection = () => {
  const { openLeadForm } = useLeadForm();
  const [activeRoom, setActiveRoom] = useState(0);

  const showroomRooms = [
    {
      name: "Sala de Estar Premium",
      description: "Ambiente sofisticado com móveis clássicos e contemporâneos",
      image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      features: [
        "Estante de madeira maciça",
        "Mesa de centro artesanal",
        "Rack para TV personalizado",
        "Aparador com detalhes entalhados"
      ],
      area: "45m²",
      style: "Clássico Contemporâneo"
    },
    {
      name: "Quarto Master Exclusivo",
      description: "Suíte completa com móveis sob medida e acabamentos únicos",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      features: [
        "Cama king size com cabeceira esculpida",
        "Guarda-roupa de 6 portas",
        "Cômoda com espelho veneziano",
        "Criados-mudos combinando"
      ],
      area: "35m²",
      style: "Luxo Artesanal"
    },
    {
      name: "Escritório Executivo",
      description: "Ambiente profissional com móveis funcionais e elegantes",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      features: [
        "Mesa executiva em madeira nobre",
        "Estante para livros modulares",
        "Poltrona ergonômica personalizada",
        "Arquivo com sistema de gavetas"
      ],
      area: "25m²",
      style: "Executivo Moderno"
    },
    {
      name: "Sala de Jantar Familiar",
      description: "Espaço acolhedor para reunir família e amigos",
      image: "https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      features: [
        "Mesa para 8 pessoas extensível",
        "Cadeiras estofadas artesanais",
        "Buffet com adega integrada",
        "Cristaleira com iluminação LED"
      ],
      area: "30m²",
      style: "Familiar Aconchegante"
    }
  ];

  const showroomInfo = {
    address: "Rua Augusta, 1234 - Consolação, São Paulo - SP",
    hours: "Segunda a Sexta: 9h às 18h | Sábados: 9h às 16h",
    phone: "(62) 99326-3202",
    email: "phmoveisconteporaneos@gmail.com",
    size: "500m²",
    parking: "Estacionamento gratuito para clientes"
  };

  return (
    <section className="py-20 bg-gradient-to-br from-muted/30 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Building" size={16} />
            <span>Showroom Virtual</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Visite Nosso Showroom
            <span className="block text-secondary">de 500m² em São Paulo</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Explore nossos ambientes decorados e veja de perto a qualidade e beleza 
            dos móveis artesanais da PH Móveis em um espaço pensado para inspirar.
          </p>
        </div>

        {/* Virtual Tour Interface */}
        <div className="bg-white rounded-3xl shadow-premium-lg border border-border overflow-hidden mb-16">
          {/* Room Navigation */}
          <div className="border-b border-border p-6">
            <div className="flex flex-wrap gap-2">
              {showroomRooms?.map((room, index) => (
                <button
                  key={index}
                  onClick={() => setActiveRoom(index)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    activeRoom === index
                      ? 'bg-primary text-primary-foreground shadow-forest'
                      : 'bg-muted text-text-primary hover:bg-primary/10 hover:text-primary'
                  }`}
                >
                  {room?.name}
                </button>
              ))}
            </div>
          </div>

          {/* Active Room Display */}
          <div className="grid lg:grid-cols-2">
            {/* Room Image */}
            <div className="relative">
              <Image
                src={showroomRooms?.[activeRoom]?.image}
                alt={showroomRooms?.[activeRoom]?.name}
                className="w-full h-96 lg:h-full object-cover"
              />
              
              {/* Virtual Tour Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Camera" size={20} />
                    <span className="text-sm font-medium">Tour Virtual 360°</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Play"
                    iconPosition="left"
                    className="border-white text-white hover:bg-white hover:text-primary"
                  >
                    Iniciar Tour
                  </Button>
                </div>
              </div>

              {/* Room Info Badge */}
              <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-premium">
                <div className="text-center">
                  <div className="text-lg font-bold text-primary">{showroomRooms?.[activeRoom]?.area}</div>
                  <div className="text-xs text-text-secondary">Área Total</div>
                </div>
              </div>
            </div>

            {/* Room Details */}
            <div className="p-8 lg:p-12">
              <div className="space-y-6">
                <div>
                  <h3 className="font-playfair text-2xl font-bold text-primary mb-2">
                    {showroomRooms?.[activeRoom]?.name}
                  </h3>
                  <p className="text-text-secondary mb-4">
                    {showroomRooms?.[activeRoom]?.description}
                  </p>
                  <div className="inline-block bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-medium">
                    {showroomRooms?.[activeRoom]?.style}
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h4 className="font-playfair text-lg font-bold text-primary mb-4">
                    Móveis em Destaque
                  </h4>
                  <div className="space-y-3">
                    {showroomRooms?.[activeRoom]?.features?.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                        <span className="text-text-secondary">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    variant="default"
                    iconName="Calendar"
                    iconPosition="left"
                    className="bg-gradient-forest hover:shadow-forest"
                  >
                    Agendar Visita
                  </Button>
                  <LeadFormButton
                    variant="outline"
                    iconName="MessageCircle"
                    iconPosition="left"
                    className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    Consultar Preços
                  </LeadFormButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Showroom Information */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Showroom Details */}
          <div className="space-y-8">
            <div>
              <h3 className="font-playfair text-3xl font-bold text-primary mb-6">
                Visite Nosso Showroom Físico
              </h3>
              <p className="text-text-secondary leading-relaxed mb-6">
                Nosso showroom de 500m² no coração de São Paulo oferece uma experiência completa 
                para você conhecer de perto a qualidade e beleza dos nossos móveis artesanais.
              </p>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <Icon name="MapPin" size={20} color="var(--color-primary)" className="mt-1" />
                <div>
                  <h4 className="font-medium text-primary">Endereço</h4>
                  <p className="text-text-secondary">{showroomInfo?.address}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Icon name="Clock" size={20} color="var(--color-primary)" className="mt-1" />
                <div>
                  <h4 className="font-medium text-primary">Horário de Funcionamento</h4>
                  <p className="text-text-secondary">{showroomInfo?.hours}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Icon name="Phone" size={20} color="var(--color-primary)" className="mt-1" />
                <div>
                  <h4 className="font-medium text-primary">Telefone</h4>
                  <p className="text-text-secondary">{showroomInfo?.phone}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Icon name="Car" size={20} color="var(--color-primary)" className="mt-1" />
                <div>
                  <h4 className="font-medium text-primary">Estacionamento</h4>
                  <p className="text-text-secondary">{showroomInfo?.parking}</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                iconName="Navigation"
                iconPosition="left"
                className="bg-gradient-forest hover:shadow-forest"
              >
                Como Chegar
              </Button>
              <LeadFormButton
                variant="outline"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              >
                Ligar Agora
              </LeadFormButton>
            </div>
          </div>

          {/* Map */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-2xl shadow-premium-lg">
              <iframe
                width="100%"
                height="400"
                loading="lazy"
                title="Localização PH Móveis Artesanais"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=-23.5505,-46.6333&z=15&output=embed"
                className="border-0"
              ></iframe>
            </div>

            {/* Map Overlay Info */}
            <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-premium">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-forest rounded-lg flex items-center justify-center">
                  <Icon name="Building" size={20} color="white" />
                </div>
                <div>
                  <div className="font-bold text-primary">PH Móveis</div>
                  <div className="text-sm text-text-secondary">Showroom São Paulo</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VirtualShowroomSection;